import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, authHeaders } from '../../contexts/AuthContext';
import SlateLogo from '../SlateLogo';
import {
  LayoutDashboard, Users, Activity, FolderKanban,
  FileText, FolderOpen, CreditCard, Receipt, Building2,
  Calendar, Bell, ChevronRight, Plus, ChevronLeft, ArrowLeft,
  BarChart3, Clock, MessageSquare, Truck, Wallet
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import UserProfileDropdown from '../UserProfileDropdown';
import { motion, AnimatePresence } from 'framer-motion';
import { demoProjects } from '../../data/dummyData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function statusColor(status) {
  switch (status) {
    case 'active': return 'text-emerald-600 bg-emerald-500/10';
    case 'completed': return 'text-zinc-500 bg-zinc-500/10';
    case 'on-hold': return 'text-amber-600 bg-amber-500/10';
    case 'at-risk': return 'text-red-600 bg-red-500/10';
    case 'draft': return 'text-zinc-400 bg-zinc-400/10';
    default: return 'text-muted-foreground bg-secondary';
  }
}

function statusDot(status) {
  switch (status) {
    case 'active': return 'bg-emerald-500';
    case 'completed': return 'bg-zinc-400';
    case 'on-hold': return 'bg-amber-500';
    case 'at-risk': return 'bg-red-500';
    case 'draft': return 'bg-zinc-300';
    default: return 'bg-zinc-400';
  }
}

const topNav = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/connections', label: 'Connections', icon: Users },
  { path: '/messages', label: 'Messages', icon: MessageSquare },
  { path: '/activity', label: 'Activity', icon: Activity },
];

const toolsNav = [
  { path: '/invoices', label: 'Invoices', icon: Receipt },
  { path: '/contracts', label: 'Contracts', icon: FileText },
  { path: '/files', label: 'Files', icon: FolderOpen },
  { path: '/payments', label: 'Payments', icon: CreditCard },
  { path: '/clients', label: 'Clients', icon: Building2 },
];

const bottomNav = [
  { path: '/notifications', label: 'Notifications', icon: Bell, highlight: true },
  { path: '/calendar', label: 'Calendar', icon: Calendar },
];

const projectSubNav = [
  { section: '', label: 'Overview', icon: LayoutDashboard },
  { section: '/recents', label: 'Recents', icon: Clock },
  { section: '/analytics', label: 'Analytics', icon: BarChart3 },
  { section: '/teams', label: 'Teams', icon: Users },
  { section: '/conversations', label: 'Conversations', icon: MessageSquare },
  { section: '/assets', label: 'Assets', icon: FolderOpen },
  { section: '/logistics', label: 'Logistics', icon: Truck },
  { section: '/accounts', label: 'Accounts', icon: Wallet },
];

function NavItem({ item, active, collapsed }) {
  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Link
            to={item.path}
            className={`flex items-center justify-center p-2 rounded-md transition-colors duration-150
              ${active ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-secondary/60'}
            `}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
          >
            <div className="relative">
              <item.icon className="w-4 h-4" strokeWidth={1.5} />
              {item.highlight && <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-red-500" />}
            </div>
          </Link>
        </TooltipTrigger>
        <TooltipContent side="right" className="text-[11px]">{item.label}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Link
      to={item.path}
      className={`flex items-center gap-2.5 px-3 py-1.5 rounded-md text-[13px] font-medium transition-colors duration-150
        ${active ? 'bg-secondary font-semibold text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-secondary/60'}
      `}
      data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <item.icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
      <span className="truncate">{item.label}</span>
      {item.highlight && <span className="ml-auto w-2 h-2 rounded-full bg-red-500 flex-shrink-0" data-testid="notification-dot" />}
    </Link>
  );
}

function SectionLabel({ children, collapsed }) {
  if (collapsed) return null;
  return <p className="px-3 pt-4 pb-1 text-[10px] uppercase tracking-wider text-muted-foreground/60 font-medium">{children}</p>;
}

function ProjectFlyout({ project, position }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -8, scale: 0.96 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: -8, scale: 0.96 }}
      transition={{ duration: 0.15 }}
      className="fixed z-[60] w-[260px] p-4 rounded-xl border border-border bg-popover shadow-xl backdrop-blur-sm"
      style={{ top: Math.min(position.top, window.innerHeight - 200), left: position.left }}
      data-testid="project-flyout"
    >
      <div className="flex items-center gap-2 mb-1.5">
        <FolderKanban className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
        <span className="font-medium text-sm truncate">{project.name}</span>
      </div>
      {project.ainaa_id && (
        <p className="text-[10px] font-mono text-muted-foreground/60 mb-2">{project.ainaa_id}</p>
      )}
      {project.description && (
        <p className="text-[11px] text-muted-foreground mb-3 line-clamp-2">{project.description}</p>
      )}
      <div className="flex items-center gap-2 text-[10px] text-muted-foreground mb-2">
        {project.project_type && <span className="px-1.5 py-0.5 rounded bg-secondary text-[9px]">{project.project_type}</span>}
        {project.status && <span className={`capitalize px-1.5 py-0.5 rounded text-[9px] font-medium ${statusColor(project.status)}`}>{project.status}</span>}
        {project.team_size > 0 && <span>{project.team_size} members</span>}
      </div>
      {project.progress !== undefined && (
        <div>
          <div className="w-full h-1.5 rounded-full bg-secondary">
            <div className="h-full rounded-full bg-[hsl(36,80%,52%)] transition-all" style={{ width: `${project.progress}%` }} />
          </div>
          <p className="text-[9px] text-muted-foreground/50 mt-1">{project.progress}% complete</p>
        </div>
      )}
    </motion.div>
  );
}

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [manualCollapsed, setManualCollapsed] = useState(false);
  const [projects, setProjects] = useState([]);
  const [flyout, setFlyout] = useState(null);
  const flyoutTimer = useRef(null);
  const sidebarRef = useRef(null);

  // Detect project context from URL
  const projectMatch = location.pathname.match(/^\/projects\/([^/]+)/);
  const activeProjectId = projectMatch ? projectMatch[1] : null;
  const inProjectMode = !!activeProjectId;
  const activeProject = projects.find(p => p.project_id === activeProjectId);

  // Get active section
  const sectionMatch = location.pathname.match(/^\/projects\/[^/]+\/(\w+)/);
  const activeSection = sectionMatch ? `/${sectionMatch[1]}` : '';

  // In project mode, main nav is always icon-only
  const collapsed = inProjectMode || manualCollapsed;

  const isActive = (path) => location.pathname === path || (path !== '/dashboard' && location.pathname.startsWith(path));

  const fetchProjects = useCallback(async () => {
    try {
      const res = await fetch(`${API}/projects`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        if (data.length > 0) {
          setProjects(data.slice(0, 8));
        } else {
          setProjects(demoProjects.slice(0, 5));
        }
      } else {
        setProjects(demoProjects.slice(0, 5));
      }
    } catch {
      setProjects(demoProjects.slice(0, 5));
    }
  }, []);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const showFlyout = (project, e) => {
    if (inProjectMode) return;
    clearTimeout(flyoutTimer.current);
    const rect = e.currentTarget.getBoundingClientRect();
    const sidebarRect = sidebarRef.current?.getBoundingClientRect();
    setFlyout({
      project,
      top: rect.top,
      left: (sidebarRect?.right || rect.right) + 8,
    });
  };

  const hideFlyout = () => {
    flyoutTimer.current = setTimeout(() => setFlyout(null), 200);
  };

  const keepFlyout = () => {
    clearTimeout(flyoutTimer.current);
  };

  return (
    <TooltipProvider delayDuration={0}>
      <div className="flex h-screen sticky top-0" ref={sidebarRef} data-testid="app-sidebar">
        {/* Main Nav Column */}
        <aside
          className={`h-full flex flex-col border-r border-border/80 transition-all duration-300 ease-in-out
            ${collapsed ? 'w-[52px]' : 'w-[240px]'}`}
          style={{ backgroundColor: 'hsl(var(--sidebar-bg))' }}
        >
          {/* Logo */}
          <div className="h-[44px] flex items-center justify-between px-3 border-b border-border/50 flex-shrink-0">
            {!collapsed ? (
              <>
                <Link to="/dashboard" className="flex items-center gap-2" data-testid="sidebar-logo">
                  <SlateLogo size={18} />
                  <span className="font-heading text-[13px] font-semibold tracking-tight">Slate</span>
                </Link>
                <button
                  onClick={() => setManualCollapsed(true)}
                  className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary/80 transition-colors"
                  data-testid="sidebar-collapse-btn"
                >
                  <ChevronLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
              </>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => { if (!inProjectMode) setManualCollapsed(false); }}
                    className="mx-auto p-1 rounded-md text-muted-foreground hover:text-foreground"
                  >
                    <SlateLogo size={18} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" className="text-[11px]">
                  {inProjectMode ? 'Slate' : 'Expand sidebar'}
                </TooltipContent>
              </Tooltip>
            )}
          </div>

          {/* Scrollable nav */}
          <nav className="flex-1 overflow-y-auto p-2 space-y-[2px]">
            {topNav.map(item => (
              <NavItem key={item.path} item={item} active={isActive(item.path)} collapsed={collapsed} />
            ))}

            <SectionLabel collapsed={collapsed}>Projects</SectionLabel>
            {collapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => { if (inProjectMode) { setManualCollapsed(false); navigate('/projects'); } else { setManualCollapsed(false); } }}
                    className={`flex items-center justify-center p-2 rounded-md transition-colors
                      ${isActive('/projects') ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-secondary/60'}`}
                    data-testid="nav-projects"
                  >
                    <FolderKanban className="w-4 h-4" strokeWidth={inProjectMode ? 2.5 : 1.5} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" className="text-[11px]">{inProjectMode ? 'Expand Projects' : 'Projects'}</TooltipContent>
              </Tooltip>
            ) : (
              <>
                {projects.map(p => (
                  <div
                    key={p.project_id}
                    onMouseEnter={(e) => showFlyout(p, e)}
                    onMouseLeave={hideFlyout}
                  >
                    <Link
                      to={`/projects/${p.project_id}`}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-[13px] transition-colors duration-150 group
                        ${location.pathname.startsWith(`/projects/${p.project_id}`)
                          ? 'bg-secondary text-foreground font-medium'
                          : 'text-muted-foreground hover:text-foreground hover:bg-secondary/60'}
                      `}
                      data-testid={`nav-project-${p.project_id}`}
                    >
                      <FolderKanban className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={1.5} />
                      <span className="truncate text-[12px]">
                        {p.name && !p.name.startsWith('Untitled') ? p.name : (
                          <span className="font-mono text-[11px] text-muted-foreground">{p.ainaa_id || 'Untitled'}</span>
                        )}
                      </span>
                      <ChevronRight className="w-3 h-3 ml-auto opacity-0 group-hover:opacity-50 flex-shrink-0 transition-opacity" strokeWidth={1.5} />
                    </Link>
                  </div>
                ))}
                <Link
                  to="/projects"
                  className="flex items-center gap-2 px-3 py-1.5 rounded-md text-[12px] text-muted-foreground/60 hover:text-muted-foreground hover:bg-secondary/60 transition-colors"
                  data-testid="nav-new-project"
                >
                  <Plus className="w-3.5 h-3.5" strokeWidth={1.5} />
                  <span>New project</span>
                </Link>
              </>
            )}

            <SectionLabel collapsed={collapsed}>Tools</SectionLabel>
            {toolsNav.map(item => (
              <NavItem key={item.path} item={item} active={isActive(item.path)} collapsed={collapsed} />
            ))}
          </nav>

          {/* Bottom */}
          <div className="p-2 border-t border-border/50 space-y-[2px] flex-shrink-0">
            {bottomNav.map(item => (
              <NavItem key={item.path} item={item} active={isActive(item.path)} collapsed={collapsed} />
            ))}
            <UserProfileDropdown user={user} collapsed={collapsed} />
          </div>
        </aside>

        {/* Project Sub-Nav Panel */}
        <AnimatePresence mode="wait">
          {inProjectMode && (
            <motion.aside
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 220, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
              className="h-full flex flex-col border-r border-border/80 overflow-hidden flex-shrink-0"
              style={{ backgroundColor: 'hsl(var(--sidebar-bg))' }}
              data-testid="project-sub-nav"
            >
              {/* Project header */}
              <div className="h-[44px] flex items-center gap-2 px-3 border-b border-border/50 flex-shrink-0">
                <button
                  onClick={() => navigate('/projects')}
                  className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary/80 transition-colors flex-shrink-0"
                  data-testid="project-back-btn"
                >
                  <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
                <div className="min-w-0 flex-1">
                  <p className="text-[12px] font-medium truncate leading-tight">
                    {activeProject?.name || 'Project'}
                  </p>
                  <p className="text-[9px] font-mono text-muted-foreground/50 truncate leading-tight">
                    {activeProject?.ainaa_id || ''}
                  </p>
                </div>
              </div>

              {/* Sub-nav items */}
              <nav className="flex-1 overflow-y-auto p-2 space-y-[2px]">
                {projectSubNav.map(item => {
                  const basePath = `/projects/${activeProjectId}`;
                  const fullPath = `${basePath}${item.section}`;
                  const isItemActive = item.section === ''
                    ? location.pathname === basePath
                    : location.pathname.startsWith(fullPath);

                  return (
                    <Link
                      key={item.section || 'overview'}
                      to={fullPath}
                      className={`flex items-center gap-2.5 px-3 py-1.5 rounded-md text-[13px] transition-colors duration-150
                        ${isItemActive
                          ? 'bg-secondary font-semibold text-foreground'
                          : 'text-muted-foreground hover:text-foreground hover:bg-secondary/60'}
                      `}
                      data-testid={`project-nav-${item.label.toLowerCase()}`}
                    >
                      <item.icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
                      <span className="truncate">{item.label}</span>
                    </Link>
                  );
                })}
              </nav>

              {/* Project quick stats */}
              {activeProject && (
                <div className="p-3 border-t border-border/50 flex-shrink-0">
                  <div className="w-full h-1.5 rounded-full bg-secondary mb-1.5">
                    <div
                      className="h-full rounded-full bg-[hsl(36,80%,52%)] transition-all"
                      style={{ width: `${activeProject.progress || 0}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between text-[9px] text-muted-foreground/50">
                    <span>{activeProject.progress || 0}% complete</span>
                    <span className={`capitalize px-1 py-px rounded text-[8px] ${statusColor(activeProject.status)}`}>{activeProject.status}</span>
                  </div>
                </div>
              )}
            </motion.aside>
          )}
        </AnimatePresence>

        {/* Flyout Portal */}
        <AnimatePresence>
          {flyout && !inProjectMode && (
            <div onMouseEnter={keepFlyout} onMouseLeave={hideFlyout}>
              <ProjectFlyout project={flyout.project} position={{ top: flyout.top, left: flyout.left }} />
            </div>
          )}
        </AnimatePresence>
      </div>
    </TooltipProvider>
  );
}
